import Pagination from "@mui/material/Pagination";
import {
  ColumnDirective,
  ColumnsDirective,
  Grid,
  GridComponent,
  Inject,
  Resize,
  Sort,
} from "@syncfusion/ej2-react-grids";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatErrorMessage, parseQueryStringToJSON } from "../../common/utils";
import { fetchRepairs } from "../../services/repairApiService";
import ProgressSpinner from "../common/ProgressSpinner";
import { IMeta, IPageQuery } from "../common/types";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { invalidateQueries } from "../../common/dataUtils";
import { Form } from "react-bootstrap";

interface IRowData {
  id: number;
  [key: string]: any;
}

const RepairList: React.FC = () => {
  const [rows, setRows] = useState<IRowData[]>([]);
  const [pagination, setPagination] = useState<IMeta>({} as IMeta);
  const [search, setSearch] = useState("");
  const [params, setParams] = useState({} as IPageQuery);
  const [pageLoaded, setPageLoaded] = useState(false);
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const location = useLocation();
  const queryClient = new QueryClient();

  useEffect(() => {
    const queryString = parseQueryStringToJSON();
    let _params = { ...params, ...queryString };
    if (location.state) {
      _params = location.state;
      setSearch(_params.query || "");
      setShowAll(_params.state === "all");
    }
    setParams(_params);
    setPageLoaded(true);
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["repairs", { params }],
    queryFn: () => fetchRepairs(params),
    enabled: pageLoaded,
  });

  useEffect(() => {
    setRows(data?.data || []);
    setPagination(data?.meta || {});
  }, [data]);

  let grid: Grid | null;

  const rowSelected = () => {
    if (grid) {
      const selected: any[] = grid.getSelectedRecords();
      if (!selected?.length) {
        return;
      }
      const id = selected[0].id;
      const options = { state: { ...params } };
      navigate(`/repairs/${id}`, options);
    }
  };

  const rowClicked = () => {
    rowSelected();
  };

  function handleOnChange(event: any): void {
    console.log(event.target.value);
    setSearch(event.target.value);
  }

  function handleKeyDown(event: any) {
    if (event.key === "Enter") {
      handleSearch();
    }
  }

  async function handleSearch() {
    console.log("search clicked", search);
    const queryString = parseQueryStringToJSON();
    const params: any = {
      state: showAll ? "all" : "",
      ...queryString,
      query: search,
    };
    setParams(params);
  }

  async function handlePaginationChange(event: any, page: number) {
    if (page == pagination.current_page) {
      return;
    }

    const queryString = parseQueryStringToJSON();
    const params: IPageQuery = {
      page: page,
      query: pagination.query,
      sort_by: pagination.sort_by,
      sort_direction: pagination.sort_direction,
      size: pagination.per_page,
      state: showAll ? "all" : "",
      ...queryString,
    };

    setParams(params);
  }

  function handleClearClick(): void {
    invalidateQueries(queryClient, ["jobs"]);
    setSearch("");
    const queryString = parseQueryStringToJSON();
    const params: any = {
      ...queryString,
      query: "",
    };
    setParams(params);
    setShowAll(false);
  }

  function handleSwitchChange(event: any): void {
    console.log("switch changed manually");
    const state = event.target.checked ? "all" : "";
    setShowAll(event.target.checked);
    setParams({ ...params, state });
  }

  if (isError) {
    return (
      <div className="row m-3">
        <div className="alert alert-danger">
          <h4 className="alert-heading">Error!</h4>
          <p>{formatErrorMessage(error.message)}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3">
        <ProgressSpinner show={isLoading} />
        <div className="page-title">Repairs</div>
        <div className="row mb-3 mt-3">
          <div className="col-md-6">
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                aria-label="Search"
                aria-describedby="basic-addon2"
                value={search}
                onChange={handleOnChange}
                onKeyDown={handleKeyDown}
              />
              <div className="input-group-append">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={handleClearClick}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <Form>
              <Form.Check
                type="switch"
                id="custom-switch"
                label="Show All Repairs"
                className="mt-2"
                onChange={handleSwitchChange}
                checked={showAll}
              />
            </Form>
          </div>
        </div>
        <div className="row my-3">
          <div className="col-md-6">
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page || 1}
              onChange={handlePaginationChange}
              shape="rounded"
            />
          </div>
          <div className="col d-flex justify-content-end mt-2 text-secondary">
            repairs found:{" "}
            <span className="ml-1"> {pagination.total || 0}</span>
          </div>
        </div>
        <div className="row mb-3" style={{ minWidth: 1000 }}>
          <GridComponent
            dataSource={rows}
            allowSorting={true}
            allowResizing={true}
            rowSelected={rowSelected}
            onClick={rowClicked}
            ref={(g) => (grid = g)}
          >
            <Inject services={[Resize, Sort]} />
            <ColumnsDirective>
              <ColumnDirective
                field="job.lead.customer_name"
                headerText="Customer Name"
              />
              <ColumnDirective field="job.code" headerText="Job" />
              <ColumnDirective field="phone" headerText="Phone" />
              <ColumnDirective field="job.lead.city" headerText="City" />
              <ColumnDirective field="called_at" headerText="Date Called" />
              <ColumnDirective field="tostart_at" headerText="Date to Start" />
              <ColumnDirective
                field="completed_at"
                headerText="Date Completed"
              />
            </ColumnsDirective>
            <Inject services={[Sort]} />
          </GridComponent>
        </div>
      </div>
    </>
  );
};

export default RepairList;
